import { useState, useEffect, useCallback } from 'react'
import { User, Plus, Trash2, RefreshCw, ChevronRight, XCircle, Loader2, Upload, Download, Mic, Play, CheckCircle2, Clock, AlertCircle, Users, Award, Search, Filter, Info } from 'lucide-react'
import { apiService, VoiceProfile, TrainingJob, TrainingConfig, TrainingConfigOptions, DEFAULT_TRAINING_CONFIG, TrainingSample, TrainingStatusType, AdapterListResponse, AdapterType, RetrainCheckResult, DuplicateProfileCheck } from '../services/api'
import { TrainingConfigPanel } from '../components/TrainingConfigPanel'
import { TrainingJobQueue } from '../components/TrainingJobQueue'
import { LossCurveChart } from '../components/LossCurveChart'
import { AddSongButton } from '../components/AddSongButton'
import { LiveTrainingMonitor } from '../components/LiveTrainingMonitor'
import { TrainingSampleUpload } from '../components/TrainingSampleUpload'
import { AdapterSelector } from '../components/AdapterSelector'
import { CheckpointBrowser } from '../components/CheckpointBrowser'
import { ConfirmActionButton } from '../components/ConfirmActionButton'
import { useToastContext } from '../contexts/ToastContext'
import clsx from 'clsx'

// Training status badge component
function TrainingStatusBadge({ status }: { status?: TrainingStatusType }) {
  if (!status) status = 'pending'

  const configs: Record<TrainingStatusType, { label: string; icon: React.ReactNode; className: string }> = {
    pending: {
      label: 'Not Trained',
      icon: <Clock size={12} />,
      className: 'bg-gray-600 text-gray-300',
    },
    training: {
      label: 'Training...',
      icon: <Loader2 size={12} className="animate-spin" />,
      className: 'bg-yellow-600 text-yellow-100',
    },
    ready: {
      label: 'Ready',
      icon: <CheckCircle2 size={12} />,
      className: 'bg-green-600 text-green-100',
    },
    failed: {
      label: 'Failed',
      icon: <AlertCircle size={12} />,
      className: 'bg-red-600 text-red-100',
    },
  }

  const config = configs[status]

  return (
    <span className={clsx('inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium', config.className)}>
      {config.icon}
      {config.label}
    </span>
  )
}

// Fork HQ pill — shown when a profile is served by the so-vits-svc-fork HQ lane.
function ForkHqBadge({ className }: { className?: string }) {
  return (
    <span
      title="Served by the so-vits-svc-fork HQ lane (stereo, native 44.1 kHz)"
      className={clsx(
        'inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-emerald-600 text-white',
        className
      )}
    >
      Fork HQ
    </span>
  )
}

function sampleQualityStatus(sample: TrainingSample): string {
  const metadata = {
    ...(sample.metadata ?? {}),
    ...(sample.extra_metadata ?? {}),
    ...(sample.quality_metadata ?? {}),
  }
  const status = metadata.qa_status ?? metadata.quality_status ?? metadata.status
  return typeof status === 'string' ? status : 'unknown'
}

function isTrainableSample(sample: TrainingSample): boolean {
  // The API permits pending QA samples with an explicit warning. Only a
  // sample that actively failed QA is blocked from training.
  return sampleQualityStatus(sample) !== 'fail'
}

function sampleQualityLabel(sample: TrainingSample): string {
  const status = sampleQualityStatus(sample)
  if (status === 'fail') return 'QA failed'
  if (status === 'warn') return 'QA warning'
  if (status === 'pass') return 'QA passed'
  return 'QA pending'
}

interface ProfileDetailProps {
  profile: VoiceProfile
  onBack: () => void
  onDelete: () => void
}

type ProfileTab = 'samples' | 'adapters' | 'checkpoints' | 'config' | 'jobs' | 'segments'

type ProfileTabItem = {
  id: ProfileTab
  label: string
}

function ProfileDetail({ profile, onBack, onDelete }: ProfileDetailProps) {
  const toast = useToastContext()
  const [detailProfile, setDetailProfile] = useState<VoiceProfile>(profile)
  const [samples, setSamples] = useState<TrainingSample[]>([])
  const [loading, setLoading] = useState(true)
  const [isDeleting, setIsDeleting] = useState(false)
  const [trainingConfig, setTrainingConfig] = useState<TrainingConfig>(DEFAULT_TRAINING_CONFIG)
  const [trainingOptions, setTrainingOptions] = useState<TrainingConfigOptions | null>(null)
  const [selectedSampleIds, setSelectedSampleIds] = useState<Set<string>>(new Set())
  const [selectedJob, setSelectedJob] = useState<TrainingJob | null>(null)
  const [startingTraining, setStartingTraining] = useState(false)
  const [activeTab, setActiveTab] = useState<ProfileTab>('samples')
  const [showAdvancedUpload, setShowAdvancedUpload] = useState(false)
  const [assignedSegments, setAssignedSegments] = useState<Array<{ type: string; segment_key: string; audio_path: string }>>([])
  const [loadingSegments, setLoadingSegments] = useState(false)
  const [adapters, setAdapters] = useState<AdapterListResponse | null>(null)
  const [selectedAdapter, setSelectedAdapter] = useState<AdapterType | null>(null)
  const [isPurging, setIsPurging] = useState(false)
  const [retrainCheck, setRetrainCheck] = useState<RetrainCheckResult | null>(null)
  const [checkingRetrain, setCheckingRetrain] = useState(false)
  const isSourceProfile = detailProfile.profile_role === 'source_artist'
  const cleanVocalMinutes = detailProfile.clean_vocal_minutes ?? ((detailProfile.clean_vocal_seconds ?? 0) / 60)
  const remainingMinutes = detailProfile.full_model_remaining_minutes ?? ((detailProfile.full_model_remaining_seconds ?? 0) / 60)
  const allowFullTraining = Boolean(detailProfile.full_model_eligible)
  const allowContinueLora = Boolean(detailProfile.has_adapter_model)
  const allowContinueFull = Boolean(detailProfile.has_full_model)
  const fullTrainingHint = allowFullTraining
    ? 'Full-model training is unlocked for this target profile.'
    : `Full-model training unlocks after 30 minutes of clean user vocals. ${Math.max(remainingMinutes, 0).toFixed(1)} minutes remaining.`
  const continuationHint = trainingConfig.training_mode === 'full'
    ? (
      allowContinueFull
        ? 'Continue training will reuse the latest full-model checkpoint or active full-model artifact.'
        : 'Continue training becomes available after this target profile has a trained full model.'
    )
    : (
      allowContinueLora
        ? 'Continue training will reuse the latest LoRA checkpoint or active adapter artifact.'
        : 'Continue training becomes available after this target profile has a trained LoRA adapter.'
    )

  const tabItems: ProfileTabItem[] = isSourceProfile
    ? [
        { id: 'samples', label: `Samples (${samples.length})` },
        { id: 'segments', label: 'Diarized Segments' },
      ]
    : [
        { id: 'samples', label: `Samples (${samples.length})` },
        { id: 'adapters', label: `Adapters (${adapters?.count ?? 0})` },
        { id: 'checkpoints', label: 'Checkpoints' },
        { id: 'segments', label: 'Diarized Segments' },
        { id: 'config', label: 'Train' },
        { id: 'jobs', label: 'Training Jobs' },
      ]

  const selectTab = (tab: ProfileTab, moveFocus = false) => {
    setActiveTab(tab)
    if (moveFocus) {
      window.requestAnimationFrame(() => {
        document.getElementById(`profile-tab-${tab}`)?.focus()
      })
    }
  }

  const handleTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, tab: ProfileTab) => {
    const currentIndex = tabItems.findIndex((item) => item.id === tab)
    if (currentIndex === -1) return

    let nextIndex: number | null = null
    if (event.key === 'ArrowRight') {
      nextIndex = (currentIndex + 1) % tabItems.length
    } else if (event.key === 'ArrowLeft') {
      nextIndex = (currentIndex - 1 + tabItems.length) % tabItems.length
    } else if (event.key === 'Home') {
      nextIndex = 0
    } else if (event.key === 'End') {
      nextIndex = tabItems.length - 1
    }

    if (nextIndex === null) return
    event.preventDefault()
    selectTab(tabItems[nextIndex].id, true)
  }

  const refreshProfile = useCallback(async () => {
    try {
      const updated = await apiService.getProfileDetails(profile.profile_id)
      setDetailProfile(updated)
    } catch (error) {
      console.error('Failed to refresh profile details:', error)
    }
  }, [profile.profile_id])

  useEffect(() => {
    const fetchSamples = async () => {
      try {
        const [data, options] = await Promise.all([
          apiService.listSamples(profile.profile_id),
          apiService.getTrainingConfigOptions().catch(() => null),
        ])
        setSamples(data)
        if (options) {
          setTrainingOptions(options)
        }
        setSelectedSampleIds(new Set(data.filter(isTrainableSample).map(sample => sample.id)))
        await refreshProfile()
      } catch (error) {
        console.error('Failed to fetch samples:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchSamples()
  }, [profile.profile_id, refreshProfile])

  // Fetch adapters on mount
  useEffect(() => {
    apiService.getProfileAdapters(profile.profile_id)
      .then(data => {
        setAdapters(data)
        setSelectedAdapter(data.selected)
      })
      .catch(err => console.error('Failed to fetch adapters:', err))
  }, [profile.profile_id])

  // Fetch assigned diarization segments when segments tab is active
  useEffect(() => {
    if (activeTab !== 'segments') return

    const fetchSegments = async () => {
      setLoadingSegments(true)
      try {
        const result = await apiService.getProfileSegments(profile.profile_id)
        setAssignedSegments(result.diarization_assignments || [])
      } catch (error) {
        console.error('Failed to fetch segments:', error)
        setAssignedSegments([])
      } finally {
        setLoadingSegments(false)
      }
    }
    fetchSegments()
  }, [profile.profile_id, activeTab])

  const handleDelete = async () => {
    setIsDeleting(true)
    try {
      await apiService.deleteProfile(profile.profile_id)
      toast.success('Profile deleted successfully')
      onDelete()
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to delete profile'
      toast.error(errorMsg)
      setIsDeleting(false)
    }
  }

  const handlePurge = async () => {
    setIsPurging(true)
    try {
      await apiService.purgeVoiceProfile(profile.profile_id)
      toast.success('Profile purged (audit records kept)')
      onDelete()
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to purge profile'
      toast.error(errorMsg)
      setIsPurging(false)
    }
  }

  const handleExport = async () => {
    try {
      const blob = await apiService.exportVoiceProfile(profile.profile_id)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${profile.profile_id}_export.json`
      a.click()
      URL.revokeObjectURL(url)
      toast.success('Profile exported')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to export profile')
    }
  }

  const handleCheckRetrain = async () => {
    setCheckingRetrain(true)
    try {
      const result = await apiService.checkRetrain(profile.profile_id)
      setRetrainCheck(result)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to check retrain status')
    } finally {
      setCheckingRetrain(false)
    }
  }

  const handleRetrainLora = async () => {
    try {
      const result = await apiService.retrainLora(profile.profile_id)
      toast.success(result.job_id ? `LoRA retrain started (job ${result.job_id})` : 'LoRA retrain started')
      await refreshProfile()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to start LoRA retrain')
    }
  }

  const handleStartTraining = async () => {
    if (samples.length === 0) {
      toast.error('No samples available. Upload samples first.')
      return
    }
    if (selectedSampleIds.size === 0) {
      toast.error('Select at least one QA-passing training sample.')
      return
    }
    setStartingTraining(true)
    try {
      const sampleIds = samples
        .filter(sample => selectedSampleIds.has(sample.id))
        .map(s => s.id)
      // Send force exactly when the panel says it will. The hint under the
      // Full Training button reads "Starting early in this single-user install
      // sends force: true", but this call never passed it - so starting Full
      // on an under-30-minute profile got a hard 400 whose message contradicted
      // the hint the user had just read. Scoped to the case the hint describes:
      // full mode, not yet unlocked. LoRA never needs it.
      const needsForce = trainingConfig.training_mode === 'full' && !allowFullTraining
      const job = await apiService.createTrainingJob(
        profile.profile_id, sampleIds, trainingConfig,
        needsForce ? { force: true } : undefined,
      )
      if (needsForce) {
        toast.warning('Started below the 30-minute clean-vocal threshold; quality may suffer.')
      }
      setSelectedJob(job)
      await refreshProfile()
      if (job.rejected_sample_ids?.length) {
        toast.warning(`${job.rejected_sample_ids.length} selected sample(s) were excluded by QA.`)
      }
      toast.success('Training job started with QA-approved samples')
      selectTab('jobs')
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to start training'
      toast.error(errorMsg)
    } finally {
      setStartingTraining(false)
    }
  }

  const handleUploadSample = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    try {
      const sample = await apiService.uploadSample(profile.profile_id, file)
      setSamples(prev => [...prev, sample])
      if (isTrainableSample(sample)) {
        setSelectedSampleIds(prev => new Set([...prev, sample.id]))
      }
      await refreshProfile()
      toast.success('Sample uploaded successfully')
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to upload sample'
      toast.error(errorMsg)
    }
    e.target.value = ''
  }

  const handleDeleteSample = async (sampleId: string) => {
    try {
      await apiService.deleteSample(profile.profile_id, sampleId)
      setSamples(prev => prev.filter(s => s.id !== sampleId))
      setSelectedSampleIds(prev => {
        const next = new Set(prev)
        next.delete(sampleId)
        return next
      })
      await refreshProfile()
      toast.success('Sample deleted successfully')
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to delete sample'
      toast.error(errorMsg)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="text-gray-400 hover:text-white"
        >
          ← Back
        </button>
        <h2 className="text-2xl font-bold">{detailProfile.name || detailProfile.profile_id}</h2>
        {detailProfile.fork_backed && <ForkHqBadge />}
      </div>

      <div className={clsx(
        'rounded-lg p-4 border',
        isSourceProfile
          ? 'bg-blue-950/30 border-blue-800 text-blue-100'
          : 'bg-emerald-950/30 border-emerald-800 text-emerald-100'
      )}>
        <div className="font-medium mb-1">
          {isSourceProfile ? 'Source Artist Profile' : 'Target User Profile'}
        </div>
        <p className="text-sm opacity-90">
          {isSourceProfile
            ? 'This profile was extracted from diarized song vocals. Use it as a source-performance reference, review its segments, or pair it with a target user profile for conversion.'
            : 'This profile represents the target user voice. Upload clean singing here, train a LoRA first, and unlock full-model training after 30 minutes of clean vocals.'}
        </p>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="text-gray-400 text-sm">Samples</div>
          <div className="text-2xl font-bold">{samples.length}</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="text-gray-400 text-sm">Profile Role</div>
          <div className="mt-1">
            <span className={clsx(
              'inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium',
              isSourceProfile ? 'bg-blue-600 text-blue-100' : 'bg-emerald-600 text-emerald-100'
            )}>
              {isSourceProfile ? 'Source artist' : 'Target user'}
            </span>
          </div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="text-gray-400 text-sm">Training Status</div>
          <div className="mt-1">
            <TrainingStatusBadge status={detailProfile.training_status} />
          </div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="text-gray-400 text-sm">Model Status</div>
          <div className="text-2xl font-bold">
            {detailProfile.active_model_type === 'full_model' ? (
              <span className="flex items-center gap-2 text-violet-400">
                <Award size={20} />
                Full
              </span>
            ) : detailProfile.has_trained_model ? (
              <span className="flex items-center gap-2 text-green-400">
                <Award size={20} />
                LoRA
              </span>
            ) : (
              'Base'
            )}
          </div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="text-gray-400 text-sm">Clean Vocals</div>
          <div className="text-2xl font-bold">
            {cleanVocalMinutes.toFixed(1)}m
          </div>
          {!isSourceProfile && (
            <div className="text-xs text-gray-500 mt-1">
              {allowFullTraining ? 'Full training unlocked' : `${Math.max(remainingMinutes, 0).toFixed(1)}m to unlock`}
            </div>
          )}
        </div>
      </div>

      {!isSourceProfile && (
        <TrainingReadinessPanel
          cleanVocalMinutes={cleanVocalMinutes}
          remainingMinutes={remainingMinutes}
          allowFullTraining={allowFullTraining}
          hasAdapterModel={Boolean(detailProfile.has_adapter_model)}
          hasFullModel={Boolean(detailProfile.has_full_model)}
          onSelectFullTraining={() => {
            setTrainingConfig((prev) => ({
              ...prev,
              training_mode: 'full',
              initialization_mode: detailProfile.has_full_model ? 'continue' : 'scratch',
            }))
            selectTab('config')
          }}
        />
      )}

      {/* Tab navigation */}
      <div className="flex gap-1 p-1 bg-gray-800 rounded-lg" role="tablist" aria-label="Profile sections">
        {tabItems.map((tab) => (
          <button
            key={tab.id}
            id={`profile-tab-${tab.id}`}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            aria-controls="profile-tabpanel"
            tabIndex={activeTab === tab.id ? 0 : -1}
            onClick={() => selectTab(tab.id)}
            onKeyDown={(event) => handleTabKeyDown(event, tab.id)}
            data-testid={`profile-tab-${tab.id}`}
            className={clsx(
              'flex-1 px-4 py-2 text-sm font-medium rounded transition-colors capitalize',
              activeTab === tab.id
                ? 'bg-gray-700 text-white'
                : 'text-gray-400 hover:text-white'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div
        role="tabpanel"
        id="profile-tabpanel"
        aria-labelledby={`profile-tab-${activeTab}`}
        tabIndex={0}
      >
      {activeTab === 'samples' && (
        <div className="bg-gray-800 rounded-lg p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Training Samples</h3>
            <div className="flex items-center gap-2">
              <AddSongButton
                profileId={profile.profile_id}
                onSongAdded={() => {
                  // Refresh samples after song is added and split
                  apiService.listSamples(profile.profile_id).then(setSamples)
                  void refreshProfile()
                }}
              />
              <button
                onClick={() => setShowAdvancedUpload(!showAdvancedUpload)}
                className={clsx(
                  'flex items-center gap-2 px-4 py-2 rounded',
                  showAdvancedUpload
                    ? 'bg-purple-600 hover:bg-purple-700'
                    : 'bg-gray-600 hover:bg-gray-500'
                )}
                title="Upload with speaker detection"
              >
                <Users size={16} />
                Smart Upload
              </button>
              <label
                htmlFor="quick-upload-sample"
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded cursor-pointer"
                title="Upload a single audio file as a training sample"
              >
                <Upload size={16} />
                Quick Upload
                <input
                  id="quick-upload-sample"
                  type="file"
                  accept="audio/*"
                  onChange={handleUploadSample}
                  className="hidden"
                  aria-label="Quick upload training sample"
                />
              </label>
            </div>
          </div>

          {/* Advanced upload with diarization */}
          {showAdvancedUpload && (
            <TrainingSampleUpload
              profileId={profile.profile_id}
              profileName={detailProfile.name}
              onSampleAdded={(sample) => {
                setSamples(prev => [...prev, sample])
                if (isTrainableSample(sample)) {
                  setSelectedSampleIds(prev => new Set([...prev, sample.id]))
                }
                setShowAdvancedUpload(false)
                void refreshProfile()
              }}
            />
          )}
          {loading ? (
            <div className="flex items-center gap-2 text-gray-400">
              <Loader2 className="animate-spin" size={16} />
              Loading...
            </div>
          ) : samples.length === 0 ? (
            <p className="text-gray-500">
              {isSourceProfile
                ? 'No extracted singer segments yet. Upload or assign diarized segments to build this source reference.'
                : 'No samples yet. Upload clean singing vocals to train this target voice profile.'}
            </p>
          ) : (
            <div className="space-y-2">
              {!isSourceProfile && (
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-700 bg-gray-900/60 p-3" data-testid="training-sample-selection-summary">
                  <div>
                    <div className="text-sm font-medium text-gray-200">
                      {selectedSampleIds.size} of {samples.filter(isTrainableSample).length} trainable samples selected
                    </div>
                    <div className="text-xs text-gray-500">
                      Samples that failed QA stay blocked; pending samples can be submitted with a quality warning.
                    </div>
                    <button
                      type="button"
                      onClick={() => selectTab('config')}
                      data-testid="go-to-training-button"
                      className="mt-2 inline-flex items-center gap-2 rounded bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700"
                    >
                      <Play size={12} />
                      Train voice model (LoRA / full) →
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setSelectedSampleIds(new Set(samples.filter(isTrainableSample).map(sample => sample.id)))}
                      className="px-3 py-1 text-xs rounded bg-gray-700 hover:bg-gray-600"
                    >
                      Select trainable
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedSampleIds(new Set())}
                      className="px-3 py-1 text-xs rounded bg-gray-700 hover:bg-gray-600"
                    >
                      Clear
                    </button>
                  </div>
                </div>
              )}
              {samples.map(sample => (
                <div key={sample.id} className="flex items-center justify-between p-3 bg-gray-750 rounded">
                  <div className="flex items-center gap-3">
                    {!isSourceProfile && (
                      <input
                        type="checkbox"
                        checked={selectedSampleIds.has(sample.id)}
                        disabled={!isTrainableSample(sample)}
                        onChange={(event) => {
                          setSelectedSampleIds(prev => {
                            const next = new Set(prev)
                            if (event.target.checked) {
                              next.add(sample.id)
                            } else {
                              next.delete(sample.id)
                            }
                            return next
                          })
                        }}
                        data-testid={`training-sample-select-${sample.id}`}
                        className="h-4 w-4 accent-blue-500 disabled:opacity-40"
                        aria-label={`Select training sample ${sample.id}`}
                      />
                    )}
                    <Mic size={16} className="text-gray-400" />
                    <div>
                      <div className="text-sm">{sample.audio_path.split('/').pop()}</div>
                      <div className="text-xs text-gray-500">
                        {sample.duration_seconds?.toFixed(1) ?? '?'}s · {sample.sample_rate}Hz · {sampleQualityLabel(sample)}
                      </div>
                    </div>
                  </div>
                  <ConfirmActionButton
                    label={<Trash2 size={14} />}
                    confirmMessage="Delete this training sample from the profile?"
                    confirmLabel="Delete"
                    onConfirm={() => handleDeleteSample(sample.id)}
                    className="p-1 text-gray-400 hover:text-red-400 bg-transparent hover:bg-transparent"
                    testId={`delete-sample-${sample.id}`}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'adapters' && (
        <div className="bg-gray-800 rounded-lg p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">LoRA Adapters</h3>
            <p className="text-sm text-gray-400">
              Select the adapter type for voice conversion
            </p>
          </div>

          <AdapterSelector
            profileId={profile.profile_id}
            value={selectedAdapter}
            onChange={(newAdapter) => setSelectedAdapter(newAdapter)}
            showMetrics={true}
            size="lg"
          />

          {adapters?.fork_engine && (
            <div
              className="rounded-lg border border-emerald-800 bg-emerald-950/30 p-3 text-sm text-emerald-100"
              data-testid="fork-engine-info"
            >
              <span className="font-medium">Fork engine</span>
              {' — '}
              speaker: {adapters.fork_engine.speaker ?? 'unknown'}
              {typeof adapters.fork_engine.trained_epochs === 'number' && ` · epochs: ${adapters.fork_engine.trained_epochs}`}
              {adapters.fork_engine.f0_method && ` · F0: ${adapters.fork_engine.f0_method}`}
            </div>
          )}

          {/* Profile Training Info */}
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div className="p-4 bg-gray-750 rounded-lg">
              <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Training Samples</div>
              <div className="text-2xl font-bold text-white">{samples.length}</div>
            </div>
            <div className="p-4 bg-gray-750 rounded-lg">
              <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Last Trained</div>
              <div className="text-lg font-medium text-white">
                {detailProfile.last_trained
                  ? new Date(detailProfile.last_trained).toLocaleDateString(undefined, {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })
                  : 'Never'}
              </div>
            </div>
            {!isSourceProfile && (
              <div className="p-4 bg-gray-750 rounded-lg">
                <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Full Training</div>
                <div className="text-lg font-medium text-white">
                  {allowFullTraining ? 'Unlocked' : `${Math.max(remainingMinutes, 0).toFixed(1)}m left`}
                </div>
              </div>
            )}
          </div>

          <div className="mt-6 p-4 bg-gray-750 rounded-lg">
            <h4 className="text-sm font-medium text-gray-300 mb-2">About Adapters</h4>
            <div className="text-sm text-gray-400 space-y-2">
              <p><strong className="text-violet-300">High Quality (HQ)</strong>: 5M+ parameters, best fidelity for song conversion. Uses full precision for maximum quality.</p>
              <p><strong className="text-yellow-300">Fast (nvfp4)</strong>: 4-bit quantized, 7x faster inference with ~20MB memory. Ideal for real-time karaoke.</p>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'checkpoints' && (
        <CheckpointBrowser
          profileId={profile.profile_id}
          onRollback={(checkpoint) => {
            void refreshProfile()
            toast.success(`Rolled back to checkpoint ${checkpoint.version}`)
          }}
        />
      )}

      {activeTab === 'config' && (
        <div className="space-y-4">
          {isSourceProfile ? (
            <div className="bg-gray-800 rounded-lg p-6 text-sm text-gray-300">
              Source artist profiles are extracted from uploaded songs and are not trainable target voices. Create or open a target user profile to train a LoRA or a full model.
            </div>
          ) : (
            <>
              <TrainingConfigPanel
                config={trainingConfig}
                onChange={setTrainingConfig}
                allowFullTraining={allowFullTraining}
                allowContinueLora={allowContinueLora}
                allowContinueFull={allowContinueFull}
                fullTrainingHint={fullTrainingHint}
                continuationHint={continuationHint}
                options={trainingOptions}
              />
              <button
                onClick={handleStartTraining}
                disabled={startingTraining || samples.length === 0 || selectedSampleIds.size === 0}
                data-testid="start-training-button"
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed rounded"
              >
                {startingTraining ? (
                  <Loader2 className="animate-spin" size={16} />
                ) : (
                  <Play size={16} />
                )}
                {trainingConfig.initialization_mode === 'continue' ? 'Continue' : 'Start'} {trainingConfig.training_mode === 'full' ? 'Full Model' : 'LoRA'} Training ({selectedSampleIds.size} samples)
              </button>

              <button
                type="button"
                onClick={handleCheckRetrain}
                disabled={checkingRetrain}
                data-testid="check-retrain-button"
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 rounded text-sm"
              >
                {checkingRetrain ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />}
                Check retrain
              </button>
              {retrainCheck && (
                <div className="rounded-lg border border-gray-700 bg-gray-900/60 p-3 space-y-3 text-sm text-gray-300" data-testid="retrain-check-result">
                  <p>
                    {retrainCheck.recommendation
                      ?? retrainCheck.reason
                      ?? (retrainCheck.needs_retrain ? 'Retrain recommended.' : 'No retrain needed.')}
                  </p>
                  {retrainCheck.recommendation && retrainCheck.reason && (
                    <p className="text-xs text-gray-500">{retrainCheck.reason}</p>
                  )}
                  {retrainCheck.needs_retrain && (
                    <ConfirmActionButton
                      label="Retrain LoRA"
                      confirmMessage="Start a LoRA retrain for this profile using its current training samples?"
                      confirmLabel="Retrain"
                      onConfirm={handleRetrainLora}
                      variant="neutral"
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white"
                      testId="retrain-lora-button"
                    />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {activeTab === 'jobs' && (
        <div className="space-y-4">
          {/* Live Training Monitor - shown when a job is running */}
          {selectedJob?.status === 'running' && (
            <LiveTrainingMonitor
              jobId={selectedJob.job_id}
              profileId={profile.profile_id}
              onComplete={() => {
                // Refresh selected job to update status
                apiService.getTrainingJob(selectedJob.job_id).then(setSelectedJob)
              }}
            />
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <TrainingJobQueue
              profileId={profile.profile_id}
              onJobSelect={setSelectedJob}
            />
            {selectedJob && selectedJob.status !== 'running' && (
              <LossCurveChart job={selectedJob} />
            )}
          </div>
        </div>
      )}

      {activeTab === 'segments' && (
        <div className="bg-gray-800 rounded-lg p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Diarized Audio Segments</h3>
            <p className="text-sm text-gray-400">
              Segments assigned to this profile from speaker diarization
            </p>
          </div>

          {loadingSegments ? (
            <div className="flex items-center gap-2 text-gray-400">
              <Loader2 className="animate-spin" size={16} />
              Loading segments...
            </div>
          ) : assignedSegments.length === 0 ? (
            <div className="text-center py-8">
              <Users className="mx-auto text-gray-500 mb-3" size={48} />
              <p className="text-gray-400 mb-2">No diarized segments assigned to this profile.</p>
              <p className="text-sm text-gray-500">
                Use the <a href="/diarization" className="text-blue-400 hover:underline">Diarization</a> page
                to analyze multi-speaker audio and assign segments to profiles.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {assignedSegments.map((segment, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-750 rounded">
                  <div className="flex items-center gap-3">
                    <Users size={16} className="text-purple-400" />
                    <div>
                      <div className="text-sm">{segment.audio_path?.split('/').pop() || segment.segment_key}</div>
                      <div className="text-xs text-gray-500">
                        Type: {segment.type} · Key: {segment.segment_key}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      </div>

      {/* Lifecycle actions */}
      <div className="flex justify-end gap-2 pt-4 border-t border-gray-700">
        <button
          type="button"
          onClick={() => void handleExport()}
          data-testid="export-profile"
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded"
        >
          <Download size={16} />
          Export
        </button>
        <ConfirmActionButton
          label={
            <span className="flex items-center gap-2">
              {isPurging ? <Loader2 className="animate-spin" size={16} /> : <XCircle size={16} />}
              Purge
            </span>
          }
          confirmMessage={`Purge profile "${detailProfile.name || detailProfile.profile_id}"? This removes profile-linked app-state and registered assets while keeping audit records.`}
          confirmLabel="Purge Profile"
          onConfirm={handlePurge}
          disabled={isPurging || isDeleting}
          pending={isPurging}
          className="flex items-center gap-2 px-4 py-2 bg-amber-700 hover:bg-amber-600 disabled:opacity-50 rounded text-white"
          testId="purge-profile"
        />
        <ConfirmActionButton
          label={
            <span className="flex items-center gap-2">
              {isDeleting ? <Loader2 className="animate-spin" size={16} /> : <Trash2 size={16} />}
              Delete Profile
            </span>
          }
          confirmMessage={`Delete profile "${detailProfile.name || detailProfile.profile_id}"? This cannot be undone.`}
          confirmLabel="Delete Profile"
          onConfirm={handleDelete}
          disabled={isDeleting}
          pending={isDeleting}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded"
          testId="delete-profile"
        />
      </div>
    </div>
  )
}

function TrainingReadinessPanel({
  cleanVocalMinutes,
  remainingMinutes,
  allowFullTraining,
  hasAdapterModel,
  hasFullModel,
  onSelectFullTraining,
}: {
  cleanVocalMinutes: number
  remainingMinutes: number
  allowFullTraining: boolean
  hasAdapterModel: boolean
  hasFullModel: boolean
  onSelectFullTraining: () => void
}) {
  const progress = Math.min((cleanVocalMinutes / 30) * 100, 100)
  const nextAction = allowFullTraining
    ? hasFullModel
      ? 'Continue full-model training for higher realtime serving fidelity.'
      : 'Train a dedicated realtime full model from scratch. Offline Seed-VC still uses reference audio, not this artifact.'
    : hasAdapterModel
      ? 'Keep collecting clean singing vocals while continuing LoRA refinement.'
      : 'Train a LoRA first, then keep uploading clean singing until full-model training unlocks.'

  return (
    <div className="rounded-lg border border-gray-700 bg-gray-800 p-4" data-testid="training-readiness-panel">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">Training readiness</h3>
          <p className="mt-1 text-sm text-gray-400">
            {cleanVocalMinutes.toFixed(1)} minutes of clean target-user singing are available. Full-model training unlocks at 30 minutes.
          </p>
        </div>
        <span className={clsx(
          'rounded-full px-3 py-1 text-xs font-medium',
          allowFullTraining ? 'bg-violet-500/20 text-violet-200' : 'bg-emerald-500/20 text-emerald-200'
        )}>
          {allowFullTraining ? 'Full model ready' : `${Math.max(remainingMinutes, 0).toFixed(1)}m remaining`}
        </span>
      </div>

      <div className="mt-4">
        <div className="mb-2 flex items-center justify-between text-sm">
          <span className="text-gray-300">Full-model unlock progress</span>
          <span className="text-gray-400">{progress.toFixed(0)}%</span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-gray-700">
          <div
            className={clsx('h-full transition-all', allowFullTraining ? 'bg-violet-500' : 'bg-emerald-500')}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
        <ReadinessStep label="LoRA" state={hasAdapterModel ? 'ready' : 'recommended'} detail={hasAdapterModel ? 'Adapter exists' : 'Best first training path'} />
        <ReadinessStep label="Full model" state={allowFullTraining ? 'ready' : 'locked'} detail={allowFullTraining ? '30m threshold met' : 'Needs more clean vocals'} />
        <ReadinessStep label="Realtime serving" state={hasFullModel ? 'ready' : 'recommended'} detail={hasFullModel ? 'Dedicated model available' : 'LoRA/full models serve realtime only'} />
      </div>

      <div className="mt-4 flex flex-col gap-3 rounded-lg bg-gray-900 p-3 md:flex-row md:items-center md:justify-between">
        <p className="text-sm text-gray-300">
          {nextAction} Quality Seed-VC offline conversion remains reference-audio driven.
        </p>
        {allowFullTraining && (
          <button
            type="button"
            onClick={onSelectFullTraining}
            data-testid="select-full-model-training"
            className="rounded bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-500"
          >
            Configure full-model training
          </button>
        )}
      </div>
    </div>
  )
}

function ReadinessStep({ label, state, detail }: { label: string; state: 'ready' | 'recommended' | 'locked'; detail: string }) {
  const className = state === 'ready'
    ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200'
    : state === 'locked'
      ? 'border-gray-700 bg-gray-900 text-gray-400'
      : 'border-amber-500/30 bg-amber-500/10 text-amber-200'

  return (
    <div className={clsx('rounded-lg border p-3', className)}>
      <div className="text-sm font-semibold">{label}</div>
      <div className="mt-1 text-xs opacity-80">{detail}</div>
    </div>
  )
}

function CreateProfileForm({ onCreated }: { onCreated: (profile: VoiceProfile) => void }) {
  const toast = useToastContext()
  const [file, setFile] = useState<File | null>(null)
  const [name, setName] = useState('')
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!file) return

    setCreating(true)
    setError(null)

    try {
      // Pass name as second parameter - backend will store it directly
      const profile = await apiService.createVoiceProfile(file, name || undefined)
      toast.success('Voice profile created successfully')
      onCreated(profile)
      setFile(null)
      setName('')
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to create profile'
      setError(errorMsg)
      toast.error(errorMsg)
    } finally {
      setCreating(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="bg-gray-800 rounded-lg p-6 space-y-4">
      <h3 className="text-lg font-semibold">Create Target User Profile</h3>

      <div>
        <label htmlFor="profile-name" className="flex items-center gap-1 text-sm text-gray-400 mb-1">
          Profile Name (optional)
          <span title="Use a descriptive name (e.g., 'My Voice', 'Singer Name'). Letters, numbers, spaces, and basic punctuation are recommended." className="cursor-help">
            <Info size={12} className="text-gray-500" />
          </span>
        </label>
        <input
          id="profile-name"
          type="text"
          data-testid="profile-name-input"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="My Voice"
          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-blue-500"
          aria-describedby="profile-name-hint"
        />
        <p id="profile-name-hint" className="mt-1 text-xs text-gray-500">
          A descriptive name helps identify this profile later
        </p>
      </div>

      <div>
        <label htmlFor="profile-audio" className="flex items-center gap-1 text-sm text-gray-400 mb-1">
          Voice Sample
          <span title="Upload a clean singing or spoken reference for the target user voice. This becomes the starting point for LoRA training and future full-model unlock progress." className="cursor-help">
            <Info size={12} className="text-gray-500" />
          </span>
        </label>
        <div className="border-2 border-dashed border-gray-600 rounded-lg p-6 text-center">
          {file ? (
            <div className="flex items-center justify-center gap-2">
              <Mic size={20} className="text-green-400" />
              <span>{file.name}</span>
              <button
                type="button"
                onClick={() => setFile(null)}
                className="text-red-400 hover:text-red-300"
                aria-label="Remove selected file"
              >
                <XCircle size={16} />
              </button>
            </div>
          ) : (
            <>
              <Upload className="mx-auto h-10 w-10 text-gray-500 mb-2" />
              <p className="text-gray-400 text-sm mb-2">Upload clean user vocals (10-30 seconds)</p>
              <input
                type="file"
                accept="audio/*"
                data-testid="profile-audio-input"
                onChange={e => setFile(e.target.files?.[0] || null)}
                className="hidden"
                id="profile-audio"
                aria-describedby="profile-audio-hint"
              />
              <label
                htmlFor="profile-audio"
                className="inline-block px-4 py-2 bg-gray-600 hover:bg-gray-500 rounded cursor-pointer"
              >
                Select File
              </label>
            </>
          )}
        </div>
        <p id="profile-audio-hint" className="mt-1 text-xs text-gray-500">
          Supported formats: MP3, WAV, FLAC, OGG. Best results come from clean isolated vocals with minimal background music.
        </p>
      </div>

      {error && (
        <div className="text-red-400 text-sm">{error}</div>
      )}

      <button
        type="submit"
        disabled={!file || creating}
        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded"
      >
        {creating ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />}
        Create Profile
      </button>
    </form>
  )
}

type ProfileFilter = 'all' | 'trained' | 'untrained'

function mergeProfiles(...groups: VoiceProfile[][]): VoiceProfile[] {
  const merged: VoiceProfile[] = []
  const seen = new Set<string>()

  for (const group of groups) {
    for (const profile of group) {
      if (!profile?.profile_id || seen.has(profile.profile_id)) {
        continue
      }
      seen.add(profile.profile_id)
      merged.push(profile)
    }
  }

  return merged
}

export function VoiceProfilePage() {
  const [profiles, setProfiles] = useState<VoiceProfile[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedProfile, setSelectedProfile] = useState<VoiceProfile | null>(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [filter, setFilter] = useState<ProfileFilter>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [duplicateWarning, setDuplicateWarning] = useState<DuplicateProfileCheck | null>(null)

  const fetchProfiles = useCallback(async () => {
    try {
      const data = await apiService.listProfiles()
      setProfiles(prev => mergeProfiles(data, prev))
    } catch (error) {
      console.error('Failed to fetch profiles:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  // Filter and search profiles
  const filteredProfiles = profiles.filter(profile => {
    // Apply training status filter
    if (filter === 'trained' && !profile.has_trained_model) return false
    if (filter === 'untrained' && profile.has_trained_model) return false

    // Apply search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      const name = (profile.name || profile.profile_id).toLowerCase()
      if (!name.includes(query)) return false
    }

    return true
  })

  useEffect(() => {
    fetchProfiles()
  }, [fetchProfiles])

  const handleProfileCreated = (profile: VoiceProfile) => {
    setProfiles(prev => mergeProfiles([profile], prev))
    setShowCreateForm(false)
    // Warn if the new profile sounds like an existing one
    apiService.checkDuplicateProfiles(profile.profile_id)
      .then((check) => {
        if (check.duplicate_warning) {
          setDuplicateWarning(check)
        }
      })
      .catch((error) => console.error('Duplicate profile check failed:', error))
  }

  const handleProfileDeleted = () => {
    setSelectedProfile(null)
    fetchProfiles()
  }

  if (selectedProfile) {
    return (
      <div className="max-w-4xl mx-auto">
        <ProfileDetail
          profile={selectedProfile}
          onBack={() => setSelectedProfile(null)}
          onDelete={handleProfileDeleted}
        />
      </div>
    )
  }

  const trainedCount = profiles.filter(p => p.has_trained_model).length
  const untrainedCount = profiles.length - trainedCount

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold">Voice Profiles</h1>
        <div className="flex gap-2">
          <button
            onClick={fetchProfiles}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded"
            title="Refresh"
          >
            <RefreshCw size={20} />
          </button>
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            data-testid="create-profile-btn"
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded"
          >
            <Plus size={16} />
            New Profile
          </button>
        </div>
      </div>

      {showCreateForm && (
        <div className="mb-6">
          <CreateProfileForm onCreated={handleProfileCreated} />
        </div>
      )}

      {duplicateWarning && (
        <div
          className="mb-4 flex items-start justify-between gap-3 rounded-lg border border-yellow-700 bg-yellow-900/30 p-4 text-sm text-yellow-100"
          data-testid="duplicate-profile-warning"
        >
          <div>
            <p className="font-medium">Possible duplicate profiles detected</p>
            <p className="mt-1 text-yellow-200/80">
              The new profile sounds similar to:{' '}
              {duplicateWarning.candidates.map((candidate) => candidate.name || candidate.profile_id).join(', ')}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setDuplicateWarning(null)}
            aria-label="Dismiss duplicate warning"
            className="text-yellow-300 hover:text-white"
          >
            <XCircle size={16} />
          </button>
        </div>
      )}

      {/* Search and Filter Controls */}
      {profiles.length > 0 && (
        <div className="mb-4 flex flex-col sm:flex-row gap-3">
          {/* Search Input */}
          <div className="flex-1">
            <label htmlFor="profile-search" className="flex items-center gap-1 text-sm text-gray-400 mb-1">
              Search Profiles
              <span title="Search by profile name or ID" className="cursor-help">
                <Info size={12} className="text-gray-500" />
              </span>
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
              <input
                id="profile-search"
                type="text"
                placeholder="Search profiles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                aria-label="Search profiles by name or ID"
              />
            </div>
          </div>

          {/* Filter Buttons */}
          <div className="flex gap-1 p-1 bg-gray-800 rounded-lg">
            <button
              onClick={() => setFilter('all')}
              className={clsx(
                'px-3 py-1.5 text-sm rounded transition-colors',
                filter === 'all'
                  ? 'bg-gray-700 text-white'
                  : 'text-gray-400 hover:text-white'
              )}
            >
              All ({profiles.length})
            </button>
            <button
              onClick={() => setFilter('trained')}
              className={clsx(
                'px-3 py-1.5 text-sm rounded transition-colors flex items-center gap-1',
                filter === 'trained'
                  ? 'bg-green-700 text-white'
                  : 'text-gray-400 hover:text-white'
              )}
            >
              <Award size={14} />
              Trained ({trainedCount})
            </button>
            <button
              onClick={() => setFilter('untrained')}
              className={clsx(
                'px-3 py-1.5 text-sm rounded transition-colors',
                filter === 'untrained'
                  ? 'bg-gray-600 text-white'
                  : 'text-gray-400 hover:text-white'
              )}
            >
              Untrained ({untrainedCount})
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="animate-spin text-gray-400" size={32} />
        </div>
      ) : profiles.length === 0 ? (
        <div className="bg-gray-800 rounded-lg p-8 text-center">
          <User className="mx-auto h-12 w-12 text-gray-500 mb-3" />
          <h3 className="text-lg font-medium mb-2">No Voice Profiles</h3>
          <p className="text-gray-400 mb-4">Create your first voice profile to get started.</p>
          <button
            onClick={() => setShowCreateForm(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded"
          >
            <Plus size={16} />
            Create Profile
          </button>
        </div>
      ) : filteredProfiles.length === 0 ? (
        <div className="bg-gray-800 rounded-lg p-8 text-center">
          <Filter className="mx-auto h-12 w-12 text-gray-500 mb-3" />
          <h3 className="text-lg font-medium mb-2">No Matching Profiles</h3>
          <p className="text-gray-400 mb-4">
            {searchQuery
              ? `No profiles match "${searchQuery}"`
              : filter === 'trained'
                ? 'No trained profiles yet. Train a profile to see it here.'
                : 'No untrained profiles.'}
          </p>
          <button
            onClick={() => { setFilter('all'); setSearchQuery(''); }}
            className="text-blue-400 hover:text-blue-300"
          >
            Clear filters
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredProfiles.map(profile => (
            <button
              key={profile.profile_id}
              onClick={() => setSelectedProfile(profile)}
              data-testid="profile-card"
              className="w-full flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-750 rounded-lg text-left transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center">
                  <User size={20} className="text-gray-400" />
                </div>
                <div>
                  <div className="font-medium">{profile.name || profile.profile_id}</div>
                  <div className="text-sm text-gray-400">
                    {profile.profile_role === 'source_artist' ? 'Source artist' : 'Target user'} · {profile.sample_count} samples
                    {profile.last_trained && ` · Trained ${new Date(profile.last_trained).toLocaleDateString()}`}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className={clsx(
                  'inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium',
                  profile.profile_role === 'source_artist'
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-emerald-100 text-emerald-800'
                )}>
                  {profile.profile_role === 'source_artist' ? 'Source' : 'Target'}
                </span>
                {profile.fork_backed && <ForkHqBadge />}
                {profile.has_trained_model && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                    <Award size={12} />
                    {profile.active_model_type === 'full_model' ? 'Full' : 'Trained'}
                  </span>
                )}
                <TrainingStatusBadge status={profile.training_status} />
                {profile.quality_score && (
                  <div className="text-sm">
                    <span className="text-gray-400">Quality:</span>{' '}
                    <span className={clsx(
                      profile.quality_score > 0.8 ? 'text-green-400' :
                      profile.quality_score > 0.6 ? 'text-yellow-400' : 'text-red-400'
                    )}>
                      {(profile.quality_score * 100).toFixed(0)}%
                    </span>
                  </div>
                )}
                <ChevronRight size={20} className="text-gray-500" />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
